import axios, { AxiosResponse } from "axios";
import {
  CreateInvoiceRequest,
  EinvoiceResponse,
  InvoiceDetail,
  InvoiceListParams,
  InvoiceListResponse,
  ProviderConfig,
  InvoiceTemplate,
  SubsystemInvoiceCreateRequest,
  SubsystemInvoiceCreateResponse,
  SubsystemInvoiceAdjustRequest,
  SubsystemInvoiceAdjustResponse,
  SubsystemInvoiceAdjustmentResult,
  GLBasedInvoiceResponse,
} from "../types";
import { KEY_LOCALSTORAGE } from "../../auth/core/_consts";
import { APP_CONFIGURATION_KEYS } from "../../utils/Constant";
import { localStorageItem } from "../../utils/LocalStorage";

// Get base URL from configuration
const getBaseUrl = (): string => {
  const integrationUrl =
    localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
      APP_CONFIGURATION_KEYS.INTEGRATION_URL
    ] ||
    process.env.REACT_APP_INTEGRATION_URL ||
    "";
  return `${integrationUrl}/einvoice`;
};

class EinvoiceService {
  private get baseUrl(): string {
    return getBaseUrl();
  }

  // Create invoice
  async createInvoice(data: CreateInvoiceRequest): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/create`, data);
    return response.data;
  }

  // Get invoice list
  async getInvoiceList(
    params: InvoiceListParams = {}
  ): Promise<EinvoiceResponse & { data: InvoiceListResponse }> {
    const url = `${this.baseUrl}/search`;
    // Map parameters according to API documentation
    const searchParams: any = {};

    if (params.page) searchParams.pageIndex = params.page;
    if (params.size) searchParams.pageSize = params.size;
    if (params.invoiceNo) searchParams.invoiceNo = params.invoiceNo;
    if (params.customerName) searchParams.customerName = params.customerName;
    if (params.customerTaxCode) searchParams.customerTaxCode = params.customerTaxCode;
    if (params.status) searchParams.status = params.status;
    if (params.ikey) searchParams.ikey = params.ikey;
    if (params.providerCode) searchParams.providerCode = params.providerCode;
    if (params.sendStatus) searchParams.sendStatus = params.sendStatus;
    if (params.fromDate) searchParams.fromDate = params.fromDate;
    if (params.toDate) searchParams.toDate = params.toDate;
    if (params.visitId) searchParams.visitId = params.visitId; // Filter by visit ID

    const response = await axios.get(url, { params: searchParams });
    return response.data;
  }

  // Get invoice detail (matches controller @GetMapping("/{ikey}"))
  async getInvoiceDetail(
    ikey: string
  ): Promise<EinvoiceResponse & { data: InvoiceDetail }> {
    const response = await axios.get(`${this.baseUrl}/${ikey}`);
    return response.data;
  }

  // Update invoice
  async updateInvoice(
    ikey: string,
    data: CreateInvoiceRequest
  ): Promise<EinvoiceResponse> {
    const response = await axios.put(`${this.baseUrl}/ikey/${ikey}`, data);
    return response.data;
  }

  // Delete invoice
  async deleteInvoice(ikey: string): Promise<EinvoiceResponse> {
    const response = await axios.delete(`${this.baseUrl}/delete/${ikey}`);
    return response.data;
  }

  // Publish invoice
  async publishInvoice(ikey: string): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/ikey/${ikey}/publish`);
    return response.data;
  }

  // Cancel invoice
  async cancelInvoice(ikey: string, reason: string): Promise<EinvoiceResponse> {
    const response = await axios.post(
      `${this.baseUrl}/ikey/${ikey}/cancel`,
      null,
      {
        params: { reason },
      }
    );
    return response.data;
  }

  // Resend/Retry invoice
  async resendInvoice(ikey: string): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/${ikey}/resend`);
    return response.data;
  }

  // Sync invoice status
  async syncInvoice(ikey: string): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/${ikey}/sync`);
    return response.data;
  }

  // Get invoice XML
  async getInvoiceXml(
    ikey: string
  ): Promise<EinvoiceResponse & { data: string }> {
    const response = await axios.get(`${this.baseUrl}/${ikey}/xml`);
    return response.data;
  }

  // Replace invoice
  async replaceInvoice(
    ikey: string,
    data: CreateInvoiceRequest
  ): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/${ikey}/replace`, data);
    return response.data;
  }

  // Adjust invoice
  async adjustInvoice(
    ikey: string,
    data: CreateInvoiceRequest,
    adjustmentType: string = "CORRECTION"
  ): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/${ikey}/adjust`, data, {
      params: { adjustmentType },
    });
    return response.data;
  }

  // Get invoice status
  async getInvoiceStatus(
    ikey: string
  ): Promise<EinvoiceResponse & { data: string }> {
    const response = await axios.get(`${this.baseUrl}/${ikey}/status`);
    return response.data;
  }

  // Check if invoice can be deleted
  async canDeleteInvoice(
    ikey: string
  ): Promise<EinvoiceResponse & { data: boolean }> {
    const response = await axios.get(`${this.baseUrl}/${ikey}/can-delete`);
    return response.data;
  }

  // Get invoice permissions
  async getInvoicePermissions(
    ikey: string
  ): Promise<EinvoiceResponse & { data: any }> {
    const response = await axios.get(`${this.baseUrl}/${ikey}/permissions`);
    return response.data;
  }

  // Download invoice PDF
  async downloadInvoicePDF(ikey: string): Promise<AxiosResponse<Blob, any>> {
    const response = await axios.get(`${this.baseUrl}/${ikey}/pdf`, {
      responseType: "blob",
      headers: {
        Accept: "application/pdf",
      },
    });
    return response;
  }

  // Get invoice logs
  async getInvoiceLogs(
    ikey: string,
    logType?: "edit" | "send"
  ): Promise<EinvoiceResponse & { data: any[]; total?: number }> {
    const url = logType
      ? `${this.baseUrl}/ikey/${ikey}/logs/${logType}`
      : `${this.baseUrl}/ikey/${ikey}/logs`;
    console.log("Fetching logs from URL:", url);

    const response = await axios.get(url);
    console.log("Logs response status:", response.status);
    console.log("Logs response data:", response.data);

    return response.data;
  }

  // Resend email
  async resendEmail(
    ikey: string,
    emails: string[],
    message?: string
  ): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/resend-email/${ikey}`, {
      emails,
      message,
    });
    return response.data;
  }

  // View PDF - opens PDF in new tab with proper authentication
  async viewPdf(ikey: string): Promise<void> {
    try {
      const url = `${this.baseUrl}/${ikey}/pdf`;
      console.log("Opening PDF URL:", url);

      // Add authentication headers by creating a form and submitting it
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        // Create a temporary link with authorization header
        const response = await axios.get(url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank");

        // Cleanup after a delay
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      } else {
        // Fallback to direct window.open
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
      throw error;
    }
  }

  // View XML - opens XML in new tab with proper authentication
  async viewXml(ikey: string): Promise<void> {
    try {
      const url = `${this.baseUrl}/${ikey}/xml`;
      console.log("Opening XML URL:", url);

      // Get XML content and display in new tab
      const response = await axios.get(url, {
        responseType: "text",
      });

      const xmlContent = response.data;
      const blob = new Blob([xmlContent], { type: "application/xml" });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank");

      // Cleanup after a delay
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (error) {
      console.error("Error opening XML:", error);
      throw error;
    }
  }

  // Download PDF
  async downloadPdf(ikey: string): Promise<Blob> {
    const response = await axios.get(`${this.baseUrl}/pdf/${ikey}`, {
      responseType: "blob",
    });
    return response.data;
  }

  // Download XML
  async downloadXml(ikey: string): Promise<string> {
    const response = await axios.get(`${this.baseUrl}/xml/${ikey}`, {
      responseType: "text",
    });
    return response.data;
  }

  // Validate invoice
  async validateInvoice(data: CreateInvoiceRequest): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/validate`, data);
    return response.data;
  }

  // Get provider configurations
  async getProviders(): Promise<EinvoiceResponse & { data: ProviderConfig[] }> {
    const response = await axios.get(`${this.baseUrl}/config/providers`);
    return response.data;
  }

  // Get invoice templates
  async getTemplates(): Promise<
    EinvoiceResponse & { data: InvoiceTemplate[] }
  > {
    const response = await axios.get(`${this.baseUrl}/config/templates`);
    return response.data;
  }

  // Get dashboard analytics
  async getDashboard(params: any = {}): Promise<EinvoiceResponse> {
    const response = await axios.get(`${this.baseUrl}/analytics/dashboard`, {
      params,
    });
    return response.data;
  }

  // Generate report
  async generateReport(data: any): Promise<EinvoiceResponse> {
    const response = await axios.post(`${this.baseUrl}/reports/generate`, data);
    return response.data;
  }

  // Get metadata for dropdowns
  async getMetadata(): Promise<EinvoiceResponse & { data: any }> {
    const response = await axios.get(`${this.baseUrl}/metadata`);
    return response.data;
  }

  // Get tax rates
  async getTaxRates(): Promise<
    EinvoiceResponse & { data: Array<{ value: number; label: string }> }
  > {
    const response = await axios.get(`${this.baseUrl}/metadata/tax-rates`);
    return response.data;
  }

  // Get product units
  async getProductUnits(): Promise<
    EinvoiceResponse & { data: Array<{ value: string; label: string }> }
  > {
    const response = await axios.get(`${this.baseUrl}/metadata/product-units`);
    return response.data;
  }

  // Get payment methods
  async getPaymentMethods(): Promise<
    EinvoiceResponse & { data: Array<{ value: string; label: string }> }
  > {
    const response = await axios.get(
      `${this.baseUrl}/metadata/payment-methods`
    );
    return response.data;
  }

  // Date validation APIs

  // Validate invoice date
  async validateInvoiceDate(data: {
    arisingDate: string;
    allowDifferentInvoiceDate: boolean;
    autoSetToLatestInvoiceDate: boolean;
  }): Promise<
    EinvoiceResponse & {
      data: {
        suggestedDate?: string;
        lastInvoiceDate?: string;
        currentDateTime?: string;
        isValid: boolean;
      };
    }
  > {
    const response = await axios.post(`${this.baseUrl}/validate-date`, data);
    return response.data;
  }

  // Get latest invoice date
  async getLatestInvoiceDate(params?: {
    supplierTaxCode?: string;
    templateCode?: string;
  }): Promise<EinvoiceResponse & { data: string }> {
    const response = await axios.get(`${this.baseUrl}/latest-invoice-date`, {
      params,
    });
    return response.data;
  }

  // Get date validation config
  async getDateValidationConfig(): Promise<
    EinvoiceResponse & {
      data: {
        allowDifferentInvoiceDate: boolean;
        autoSetToLatestInvoiceDate: boolean;
        lastInvoiceDate?: string;
      };
    }
  > {
    const response = await axios.get(`${this.baseUrl}/date-validation-config`);
    return response.data;
  }

  // USB Token Signing APIs

  // Get hash string for USB Token signing (Step 1)
  async getUSBTokenHash(data: {
    invoiceRequest: CreateInvoiceRequest;
    certificateSerial: string;
    adjustedNote?: string;
    originalInvoiceType?: string;
    originalTemplateCode?: string;
    validation?: number;
    timeoutSeconds?: number;
  }): Promise<
    EinvoiceResponse & {
      data: {
        hashString: string;
        transactionUuid: string;
        supplierTaxCode: string;
        templateCode: string;
        generatedAt: number;
      };
    }
  > {
    const response = await axios.post(
      `${this.baseUrl}/usb-token/get-hash`,
      data
    );
    return response.data;
  }

  // Insert signature and create invoice (Step 2)
  async insertUSBTokenSignature(data: {
    supplierTaxCode: string;
    templateCode: string;
    hashString: string;
    signature: string;
  }): Promise<
    EinvoiceResponse & {
      data: {
        success: boolean;
        ikey: string;
        invoiceNo: string;
        reservationCode: string;
        transactionID: string;
        linkView: string;
        message: string;
        processedAt: string;
      };
    }
  > {
    const response = await axios.post(
      `${this.baseUrl}/usb-token/insert-signature`,
      data
    );
    return response.data;
  }

  // Check USB Token status
  async getUSBTokenStatus(): Promise<
    EinvoiceResponse & {
      data: {
        usbTokenAvailable: boolean;
        certificateSerial: string;
        certificateExpiry: string;
        lastUsed: string;
      };
    }
  > {
    const response = await axios.get(`${this.baseUrl}/usb-token/status`);
    return response.data;
  }

  // Retry failed USB Token signing
  async retryUSBTokenSigning(
    transactionUuid: string
  ): Promise<EinvoiceResponse> {
    const response = await axios.post(
      `${this.baseUrl}/usb-token/retry/${transactionUuid}`
    );
    return response.data;
  }

  // ========== SUBSYSTEM INVOICE INTEGRATION ==========

  /**
   * Tạo hóa đơn điện tử từ phân hệ
   * Endpoint: POST /einvoice/subsystem
   * @param request Danh sách invoice codes và thông tin phân hệ
   */
  async createInvoicesFromSubsystem(
    request: SubsystemInvoiceCreateRequest
  ): Promise<EinvoiceResponse & { data: SubsystemInvoiceCreateResponse }> {
    const integrationUrl =
      localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
        APP_CONFIGURATION_KEYS.INTEGRATION_URL
      ] ||
      process.env.REACT_APP_INTEGRATION_URL ||
      "";

    const url = `${integrationUrl}/einvoice/subsystem`;
    const response = await axios.post(url, request);
    return response.data;
  }

  /**
   * Tạo hóa đơn subsystem (hóa đơn gộp)
   * Dùng cho retry manual từ danh sách hóa đơn
   * Endpoint: POST /einvoice/subsystem
   * @param request Danh sách invoice codes và code phân hệ
   */
  async createSubsystemInvoice(request: {
    invoiceCodes: string[];
    subsystemCode: string;
  }): Promise<EinvoiceResponse & { data: SubsystemInvoiceCreateResponse }> {
    const integrationUrl =
      localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
        APP_CONFIGURATION_KEYS.INTEGRATION_URL
      ] ||
      process.env.REACT_APP_INTEGRATION_URL ||
      "";

    const url = `${integrationUrl}/einvoice/subsystem`;

    // Convert to SubsystemInvoiceCreateRequest format
    const subsystemRequest: SubsystemInvoiceCreateRequest = {
      invoiceCodes: request.invoiceCodes,
      subsystemCode: request.subsystemCode,
    };

    const response = await axios.post(url, subsystemRequest);
    return response.data;
  }

  /**
   * Điều chỉnh hóa đơn điện tử từ phân hệ
   * Endpoint: POST /einvoice/subsystem/adjust
   * @param request Thông tin hóa đơn gốc và hóa đơn điều chỉnh
   */
  async adjustInvoicesFromSubsystem(
    request: SubsystemInvoiceAdjustRequest
  ): Promise<EinvoiceResponse & { data: SubsystemInvoiceAdjustResponse }> {
    const integrationUrl =
      localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
        APP_CONFIGURATION_KEYS.INTEGRATION_URL
      ] ||
      process.env.REACT_APP_INTEGRATION_URL ||
      "";

    const url = `${integrationUrl}/einvoice/subsystem/adjust`;

    const response = await axios.post(url, request);
    console.log("🔧 Subsystem adjust response:", response.data);
    return response.data;
  }

  /**
   * Tạo hóa đơn điện tử từ GL entries cho 1 visit (GL-BASED METHOD - NEW)
   * Endpoint: POST /einvoice/subsystem/gl/{visitId}
   * @param visitId Visit UUID
   * @param request SubsystemInvoiceCreateRequest (chứa subsystemCode)
   */
  async createInvoicesFromVisitGL(
    visitId: string,
    request: SubsystemInvoiceCreateRequest
  ): Promise<EinvoiceResponse & { data: GLBasedInvoiceResponse }> {
    const integrationUrl =
      localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
        APP_CONFIGURATION_KEYS.INTEGRATION_URL
      ] ||
      process.env.REACT_APP_INTEGRATION_URL ||
      "";

    const url = `${integrationUrl}/einvoice/subsystem/gl/${visitId}`;

    const response = await axios.post(url, request);
    console.log("📊 GL-based invoice response:", response.data);
    return response.data;
  }

  /**
   * Retry invoice từ ikey
   * - Với GL-based invoice: tự động lấy visitId từ metadata và retry
   * - Với Subsystem invoice: tự động lấy invoice codes và retry
   * Endpoint: POST /einvoice/subsystem/retry/{ikey}
   * @param ikey Invoice ikey cần retry
   */
  async retryInvoiceFromIkey(ikey: string): Promise<EinvoiceResponse & { data: string }> {
    const integrationUrl =
      localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
        APP_CONFIGURATION_KEYS.INTEGRATION_URL
      ] ||
      process.env.REACT_APP_INTEGRATION_URL ||
      "";

    const url = `${integrationUrl}/einvoice/subsystem/retry/${ikey}`;

    const response = await axios.post(url);
    console.log("🔄 Retry invoice response:", response.data);
    return response.data;
  }
}

export const einvoiceService = new EinvoiceService();
export default einvoiceService;
